import { ChatBubbleOutline } from "@mui/icons-material";
import { Box, IconButton, Popover, TextField, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

const CommentButton = ({
  item,
  isPrimary,
  onSave,
  isAdminScorecardView = false,
  allComments = [],
  currentUser = {},
  disabled = false,
  userAssessView = false,
  myScorecardView = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();
  console.log("User Assess View::", userAssessView);

  // Keep local state in sync with props if they change externally
  const [comment, setComment] = useState(item.comment || "");

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  //  1. Memoize filtered comments to prevent recalculation on every render
  const myCommentObj = useMemo(() => {
    // Find the single comment where isMyComment is true
    return allComments.find((c) => c.myComment === true);
  }, [allComments]);

  const othersComments = useMemo(() => {
    // Filter for all comments where isMyComment is false
    return allComments.filter((c) => c.myComment === false);
  }, [allComments]);

  useEffect(() => {
    if (isAdminScorecardView) {
      // In Admin View, the editable text is the current user's specific comment
      setComment(myCommentObj?.comment || "");
    } else {
      // In Standard View, the text is the general item comment
      setComment(item.comment || "");
    }
  }, [isAdminScorecardView, myCommentObj, item.comment]);

  const isOpen = Boolean(anchorEl);

  return (
    <>
      <IconButton size="small" onClick={handleOpen} disabled={disabled}>
        <ChatBubbleOutline
          fontSize="small"
          sx={{
            // Logic:
            // 1. If it has comments -> Blue (#1A73E8)
            // 2. If it's empty -> Darker Grey (#757575) to match the icon before it
            color: item.comment || allComments.length > 0 ? "#00717D" : "#757575",

            // Force opacity to 1 so it doesn't look "faded" even when the button is disabled
            opacity: 1,
          }}
        />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff !important", // Force solid white
            opacity: 1, // Ensure no transparency
            backgroundImage: "none", // Remove MUI dark mode overlays
            boxShadow: "0px 8px 24px rgba(0,0,0,0.2)",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            width: 320,
            zIndex: 1300, // Ensure it sits above table headers
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: "grey.900" }}>
            {myScorecardView ? t("user.assess.primaryAssessorComment") : t("user.assess.comments")}
          </Typography>

          {isAdminScorecardView && othersComments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {othersComments.map((c, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 1.5,
                    p: 1,
                    pr: 1.5, // 1. Important: Add right padding so the scrollbar doesn't cover the text
                    border: "1px dashed #d0d0d0",
                    bgcolor: "#f9f9f9",
                    borderRadius: "4px",
                    maxHeight: 70, // Fixed height to trigger scrolling

                    // 2. Only show scrollbar when text exceeds 60px
                    overflowY: "auto",
                    overflowX: "hidden",
                    cursor: "default",

                    // 3. Make the scrollbar thumb darker so it is actually visible
                    "&::-webkit-scrollbar": {
                      width: "5px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#b0b0b0", // Darker gray so you can see it on #f9f9f9
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "grey.800",
                      display: "block",
                      fontSize: "0.75rem",
                      mb: 0.2,
                    }}
                  >
                    {c.commenterName}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "grey.700",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      // 4. Force text to wrap so it grows vertically and triggers the scrollbar
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      ml: 0.5,
                    }}
                  >
                    {c.comment}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {isPrimary && !userAssessView ? (
            <Box>
              {isAdminScorecardView && (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "grey.800", mb: 0.5, display: "block" }}
                >
                  {t("user.assess.myComment")}
                </Typography>
              )}
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                size="small"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onBlur={() => {
                  // Compare against the correct original value based on view mode
                  const original = isAdminScorecardView
                    ? myCommentObj?.comment || ""
                    : item.comment || "";
                  // if (original === comment) return;
                  if (original.trim() === comment.trim()) {
                    return;
                  }
                  onSave(comment);
                }}
                placeholder={t("user.assess.write")}
                autoFocus
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    backgroundColor: "#ffffff",
                    color: "grey.800", // Consistent input text color
                    "& fieldset": { borderColor: "#e0e0e0" },
                  },
                }}
              />
            </Box>
          ) : (
            <>
              {/* Only render this whole block if a comment actually exists */}
              {
                (isAdminScorecardView ? myCommentObj?.comment : item.comment) ? (
                  <Box>
                    {/* Show label if user is looking at their own comment area */}
                    {isPrimary && isAdminScorecardView && (
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "grey.800", mb: 0.5, display: "block" }}
                      >
                        {t("user.assess.myComment")}
                      </Typography>
                    )}

                    <Typography
                      variant="body2"
                      sx={{
                        p: 1.5,
                        bgcolor: "#f5f5f5",
                        borderRadius: 1,
                        color: "grey.700",
                        minHeight: 60,
                        whiteSpace: "pre-wrap", // Respect line breaks
                      }}
                    >
                      {isAdminScorecardView ? myCommentObj.comment : item.comment}
                    </Typography>
                  </Box>
                ) : null /* If no comment, render nothing at all */
              }
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

CommentButton.propTypes = {
  item: PropTypes.shape({
    comment: PropTypes.string,
    kpiAssignmentsId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    performanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  isPrimary: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  isAdminScorecardView: PropTypes.bool,
  allComments: PropTypes.array,
  currentUser: PropTypes.object,
  disabled: PropTypes.bool,
  userAssessView: PropTypes.bool,
  myScorecardView: PropTypes.bool,
};

CommentButton.defaultProps = {
  item: { comment: "" },
  isPrimary: false,
  isAdminScorecardView: false,
  allComments: [],
  currentUser: {},
  userAssessView: false,
  myScorecardView: false,
};

export default CommentButton;
